/* eslint-disable @typescript-eslint/no-unused-vars */
import { Button } from '@beek/design-system';
import { DeviceSizes, InputForm, Typography } from 'beek-design-system';
import { FormEvent, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import Avo from 'src/tracking/Avo';
import Const from 'src/config/Const';
import Link from 'next/link';
import authenticateUserWithBeek from 'src/app/auth/authenticateUserWithBeek';
import emailRegex from 'src/utils/emailRegex';
import pathWithQueryString from 'src/common/utils/pathWithQueryString';
import selectExperiment from 'src/common/utils/selectExperiment';
import styled from 'styled-components';
import toast from 'react-hot-toast';

const SignUpContainer = styled.form`
  align-self: center;
  border: none;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-bottom: 3.5rem;
  margin: 0 auto;
  max-width: 400px;
  text-align: left;
  width: 90%;
`;

const SignUpTitle = styled(Typography)`
  margin-bottom: 0.5rem;
  align-self: center;
`;

const SignUpSubtitle = styled(Typography)`
  margin-bottom: 2.5rem;
  align-self: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 22px;
  margin-top: 40px;
  width: 100%;
`;

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
`;

const LoginText = styled(Typography)`
  font-weight: bold;
`;

const TermsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;
  width: 15.313rem;
  height: 2rem;
  @media ${DeviceSizes.laptop} {
    width: 15.375rem;
  }
`;

const LinksContainer = styled.div`
  display: flex;
`;

const SignupForm = (): JSX.Element => {
  const router = useRouter();
  const [routesExperiment, setRoutesExperiment] = useState(Const.beekApiSignUp);
  const [inputPasswordType, setInputPasswordType] = useState('password');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formIsValid, setFormIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isValidEmail = emailRegex.test(email);
  const isValidPassword = password.length > 5;
  const isValidName = name.length > 0;

  const t = useTranslations('auth');

  const { planId, offer } = router.query;

  const signup = async (): Promise<void> => {
    setIsLoading(true);

    const payload = {
      email,
      password,
      name,
    };

    try {
      const userData = await authenticateUserWithBeek(
        routesExperiment,
        payload
      );

      Avo.userCreated({
        userId_: userData?.data.user_id,
        email: payload.email,
        firstName: payload.name,
        lastName: undefined,
        gender: undefined,
        createdAt: Date.now().toString(),
        provider: 'Email',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      const redirect_path = pathWithQueryString('/welcome/choose-your-path', {
        planId,
        offer,
      });

      router.push(redirect_path);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error(error.message);
    }

    setIsLoading(false);
  };

  const handleSubmit = (
    e?: FormEvent<HTMLFormElement | HTMLAnchorElement | HTMLButtonElement>
  ): void => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (formIsValid) signup();
  };

  useEffect(() => {
    const renderExperiment = async () => {
      const experiment = await selectExperiment(
        Const.routesExperiment,
        Const.beekApiSignUp,
        Const.beekAuthSignUp
      );
      setRoutesExperiment(experiment);
    };
    renderExperiment();
  }, []);

  useEffect(() => {
    if (isValidEmail && isValidPassword && isValidName) {
      setFormIsValid(true);
    } else {
      setFormIsValid(false);
    }
  }, [isValidEmail, isValidPassword, isValidName]);

  return (
    <SignUpContainer onSubmit={handleSubmit}>
      <SignUpTitle type="headline3">{t('signup.title') as string}</SignUpTitle>
      <SignUpSubtitle type="body1" colorVariant="gray70">
        {t('signup.subtitle') as string}
      </SignUpSubtitle>
      <Typography type="caption" marginBottom="0.25rem">
        {t('signup.name') as string}
      </Typography>
      <InputForm
        marginBottom="1.5rem"
        type="text"
        name="name"
        isValid
        value={name}
        setInputValue={setName}
      />
      <Typography type="caption" marginBottom="0.25rem">
        {t('signup.email') as string}
      </Typography>
      <InputForm
        marginBottom="1.5rem"
        type="email"
        name="email"
        isValid={isValidEmail || email.length === 0}
        value={email}
        setInputValue={setEmail}
        labelError={t('errors.email') as string}
      />
      <Typography type="caption" marginBottom="0.25rem">
        {t('signup.password') as string}
      </Typography>
      <InputForm
        type={inputPasswordType}
        setInputPasswordType={setInputPasswordType}
        name="password"
        isValid={isValidPassword || password.length === 0}
        value={password}
        setInputValue={setPassword}
        labelError={t('errors.passwordInput') as string}
        showLabelError
      />
      <ButtonContainer>
        <Button
          onClick={handleSubmit}
          disabled={!formIsValid}
          loading={isLoading}
        >
          {t('signup.signupButton') as string}
        </Button>
      </ButtonContainer>
      <LoginContainer>
        <Typography type="body2" marginRight="0.516rem">
          {t('signup.haveAccount') as string}
        </Typography>
        <Link href={pathWithQueryString('/login', { offer })}>
          <a>
            <LoginText textDecoration="underline" type="body2">
              {t('signup.login') as string}
            </LoginText>
          </a>
        </Link>
      </LoginContainer>
      <TermsContainer>
        <Typography type="caption">
          {t('signup.acceptTerms') as string}
        </Typography>
        <LinksContainer>
          <Link
            href="/aviso_privacidad.pdf"
            target="_blank"
            rel="noopenner noreferrer"
            locale={false}
            passHref
          >
            <a target="_blank" rel="noopener noreferrer">
              <Typography textDecoration="underline" type="caption">
                {t('signup.privacy') as string}
              </Typography>
            </a>
          </Link>
          <Typography type="caption" marginRight="0.25rem" marginLeft="0.25rem">
            {t('signup.and') as string}
          </Typography>
          <Link href="/terms.pdf" locale={false} passHref>
            <a target="_blank" rel="noopener noreferrer">
              <Typography textDecoration="underline" type="caption">
                {t('signup.terms') as string}
              </Typography>
            </a>
          </Link>
        </LinksContainer>
      </TermsContainer>
    </SignUpContainer>
  );
};
export default SignupForm;
